import React, {
    createContext,
    useContext,
    useEffect,
    useState
} from "react";
import { Storage } from "../storage";
import { AuthService } from "../services/auth";
import api from "../services/api";
import { AxiosError, InternalAxiosRequestConfig } from "axios";
import { io } from "socket.io-client";
import { Constants } from "../utils";
import { useMediaQuery } from "@mui/material";

export interface IUser {
    name: string
    cpf: string
    role: number
    data: {
        phone: string
        address: {
            cep: string
            city: string
            district: string
            number: string
            street: string
            uf: string
        }
        birthday: string
    }
    lastLogin: string
    createdAt: string
    uuid: string
    enterprise: {
        document: string
        name: string
        data: {
            qsa: Array<{
                nome: string
                qual: string
            }>
            birthday: string
            socialReason: string
            size: string
            situation: string
            address: {
                street: string
                number: string
                city: string
                district: string
                uf: string
                cep: string
            }
            email: string
            phone: string
            cnae: Array<string>
        }
        userId: number
        createdAt: string
    }
    client: {
        id: number
        actualTable: number
        totalPaid: number
        totalLateCharges: number
        status: number
        lastVerification: string
        lastScoreSum: number
        addressFileId: number
        nextVerification: string
        canRequestAfter: string
        needVerifyPhone: boolean
        needToTakePictures: boolean
        needSendSerasa: boolean
        partnerId?: number,
        serasaFileId?: number
        serasaFileStatus?: string
        serasaReceiptId?: number
        welcomeLocked: boolean
        lastUpdate: string
        blockedReason: {
            action: string
            code: string
            message: string
            reason: string
            at: string
        }[] | null
    }
    actualContract: {
        contract: {
            id: number
            uuid: string
            clientId: number
            commissionPaid: boolean
            haveCommission: boolean
            netAmount: number
            groosAmount: number
            total: number
            rent: number
            parcels: number
            lateCharges: number
            totalPaid: number
            totalDebit: number
            payTo: string
            status: number
            integrations?: {
                sign: {
                    requisitionId: string
                    url: string
                    key: string
                    vector: string
                    ticket: string
                    allTickets: {
                        "1": string
                    }
                    duplicateRequisitions: any,
                    createdAt: string
                }
            }
            createdAt: string
            paidAt: any
            canceledAt: any
            canceledReason: any
            contractFileId: number | null,
            contractFileUrl: null | string
        }
        parcels: Array<IParcels>
    }
    account?: {
        id: number
        userId: number
        type: number
        balance: number
        status: number
        createdAt: string
    }
}

export interface IParcels {
    id: number
    index: number

    netAmount: number
    rent: number
    groosAmount: number

    taxes: Record<string, any>,
    fees: number
    fine: number
    legalCharges: number
    totalLateCharges: number
    discountedValue: number

    total: number

    paidAt?: string
    dueAt: string,
    status: number
}

interface IModalProps {
    type: "success" | "error" | "warning",
    message: JSX.Element | string,
    callback?: () => void
}

type TIndicationTab = "share" | "rewards" | "extract" | "defaulted_contracts"

interface IIndicationTabHistory {
    index: number,
    name: TIndicationTab
}

interface ErrorResponse {
    message: { error: string } | string
    point: string
    status: number
}

interface ContextProps {
    isAuthenticated: boolean | null,
    setIsAuthenticated: (value: boolean) => void,
    user: IUser | null,
    setUser: (data: IUser | null) => void,
    getData: () => Promise<void>,
    tab: number,
    setTab: (value: number) => void,
    handleModal: (open: boolean, message?: IModalProps) => void,
    openModalError: boolean,
    errorMessage: IModalProps | null,
    indicationTab: TIndicationTab,
    handleIndicationTab: (to: TIndicationTab) => void,
    handleBack: () => void;
    isMobile: boolean
}


export const AuthManager = createContext({} as ContextProps);

const useAuth = () => useContext(AuthManager);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
    const [user, setUser] = useState<IUser | null>(null)
    const [tab, setTab] = useState(0)
    const isMobile = !useMediaQuery("(min-width: 948px)")


    const [openModalError, setOpenModalError] = useState(false)
    const [errorMessage, setErrorMessage] = useState<null | IModalProps>(null)

    const [indicationTab, setIndicationTab] = useState<TIndicationTab>("share")

    const [indicationTabHistory, setIndicationTabHistory] = useState<IIndicationTabHistory[]>([
        {
            index: 0,
            name: "share"
        }
    ])

    const connectWebSocket = async () => {
        const tokens = await Storage.getTokens()
        const socket = io(Constants.urls.socket, {
            auth: {
                token: tokens?.access as string
            },
            autoConnect: true,
            transports: ["websocket"],
            withCredentials: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 5000,
            timeout: 10000
        })
        if (socket) {
            socket.on("connect", () => {
                console.log("🔗 Conectado ao WebSocket")
                socket.emit("listen")
            })

            socket.on("connect_error", (err) => {
                console.error("❌ Erro de conexão:", err.message)
            })

            socket.on("listen", (...args) => {
                console.log("🔔 Evento recebido:", args)
                if (args[0]?.event === "update-contract") {
                    getData()
                }
            })

        } else {
            setTimeout(() => {
                return connectWebSocket()
            }, 1000)
        }
    }

    useEffect(() => {
        (async () => {
            const token = await Storage.getTokens()
            const userStorage = await Storage.getUser()

            if (!token) {
                setIsAuthenticated(false);
            } else {
                setIsAuthenticated(true);
                if (userStorage) setUser(userStorage);
            }
        })()

        document.addEventListener("visibilitychange", async function () {
            if (document.visibilityState === 'visible') {
                getData()
            }
        });
    }, [])

    useEffect(() => {
        if (isAuthenticated) {
            getData()
            connectWebSocket()
        }
    }, [isAuthenticated])


    const getData = async (): Promise<void> => {
        await AuthService.me()
            .then(({ data }) => {
                setUser(data)
                Storage.saveUser(data)
            })
    }

    const handleModal = (open: boolean, message?: IModalProps) => {
        setOpenModalError(open)
        if (open && message) {
            setErrorMessage(message)
        } else {
            setErrorMessage(null)
        }
    }

    const handleIndicationTab = (to: TIndicationTab) => {
        const lastIndex = indicationTabHistory.reduce((max, item) => item.index > max ? item.index : max, indicationTabHistory[0].index)
        setIndicationTabHistory(prev => ([
            ...prev,
            {
                index: lastIndex + 1,
                name: to
            }
        ]))
        setIndicationTab(to)
    }

    const handleBack = () => {
        if (indicationTabHistory.length > 1) {
            setIndicationTabHistory(prev => {
                const newHistory = [...prev];
                newHistory.pop(); // Remove o último item
                const lastTab = newHistory[newHistory.length - 1]; // Obtém o novo último item
                setIndicationTab(lastTab.name);
                return newHistory;
            });
        } else {
            setTab(0)
        }
    };

    api.interceptors.response.use(e => e, async (reject: AxiosError) => {
        if (reject?.status && reject?.status > 300) {
            const errorResponse = reject.response?.data as ErrorResponse

            if (errorResponse?.message !== "Acesso negado") {
                if (errorResponse.message) {
                    if (errorResponse.message !== "Envie um header Authorization" && !reject?.config?.url?.includes("/users/clients/zone")) handleModal(true, { message: errorResponse.message as string, type: "error" })
                } else {
                    handleModal(true, { message: "Ocorreu um erro inesperado.", type: "error" })
                }
            }

            if (reject?.response?.status === 401 && typeof errorResponse?.message === "object" && errorResponse?.message?.error === "jwt expired") {

                if (reject?.config?.url === "/users/redefine-password") {
                    handleModal(true, { message: "Token expirado!", type: "error" })
                    return
                }

                try {
                    const tokens = await Storage.getTokens();

                    const { data } = await AuthService.refresh(tokens.refresh);

                    Storage.saveTokens({
                        refresh: tokens.refresh,
                        access: data.access
                    });

                    const originalRequest = reject.config as InternalAxiosRequestConfig<any>;
                    originalRequest.headers['Authorization'] = `Bearer ${data.access}`;

                    return api(originalRequest);

                } catch (error) {
                    AuthService.logout()
                }
            }
        }
        return Promise.reject(reject);
    })

    async function intercepter(config: InternalAxiosRequestConfig<any>) {
        const tokens = await Storage.getTokens();
        if (tokens) {
            config.headers.Authorization = `Bearer ${tokens?.access}`;
        }
        return config;
    }

    api.interceptors.request.use(intercepter);

    return (
        <AuthManager.Provider value={{
            isAuthenticated,
            setIsAuthenticated,
            user,
            setUser,
            getData,
            setTab,
            tab,
            errorMessage,
            handleModal,
            openModalError,
            handleIndicationTab,
            indicationTab,
            handleBack,
            isMobile
        }}>
            {children}
        </AuthManager.Provider>
    )
}

export default useAuth;